import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  getClubTeams,
  getClubCoaches,
  createTeam,
  getAgeGroups,
  getClubSports,
  getClubScope,
  type ClubCoach,
  type ClubScopeInfo,
  type AgeGroup,
  type ClubSport,
} from "../../services/api";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import toast from "react-hot-toast";
import TeamJoinLinks from "../../components/club/TeamJoinLinks";
import TeamProducts from "../../components/club/TeamProducts";

type Team = { id: string; name: string; age_group?: string | null; sport_type?: string | null; coach_id?: string | null };

export default function ClubTeams() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClubId } = useAuth();

  const [teams, setTeams] = useState<Team[]>([]);
  const [coaches, setCoaches] = useState<ClubCoach[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [sports, setSports] = useState<ClubSport[]>([]);
  const [scope, setScope] = useState<ClubScopeInfo | null>(null);
  const [linksFor, setLinksFor] = useState<Team | null>(null);
  const [productsFor, setProductsFor] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", age_group_id: "", sport_id: "", coach_id: "", min_age: "" });

  useEffect(() => {
    if (!activeClubId) return;

    (async () => {
      setLoading(true);
      try {
        const [teamsData, coachesData, ageGroupsData, sportsData, scopeData] = await Promise.all([
          getClubTeams(activeClubId),
          getClubCoaches(activeClubId),
          getAgeGroups().catch(() => [] as AgeGroup[]),
          getClubSports(activeClubId).catch(() => [] as ClubSport[]),
          getClubScope(activeClubId).catch(() => null),
        ]);
        setTeams(teamsData);
        setCoaches(coachesData);
        setAgeGroups(ageGroupsData);
        setScope(scopeData);

        // A coordinator can only create teams in the sports they run, so the
        // picker offers exactly those — and preselects when there is one.
        const allowed =
          scopeData && scopeData.sport_ids !== null
            ? sportsData.filter((sp) => scopeData.sport_ids!.includes(sp.id))
            : sportsData;
        setSports(allowed);
        if (allowed.length === 1) {
          setTeamForm((f) => ({ ...f, sport_id: allowed[0].id }));
        }
      } catch (error) {
        console.error("Failed to load teams:", error);
      }
      setLoading(false);
    })();
  }, [activeClubId]);

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    // Coach is optional — teams.coach_id is nullable in the schema and
    // the backend already accepts a null. A club_admin can set up the
    // team roster first and assign a coach later via /admin/teams.
    if (!activeClubId || !teamForm.name) return;

    setCreating(true);
    try {
      const parsedMinAge = teamForm.min_age.trim() === "" ? null : Number(teamForm.min_age);
      const newTeam = await createTeam(activeClubId, {
        name: teamForm.name,
        age_group_id: teamForm.age_group_id || null,
        sport_id: teamForm.sport_id || undefined,
        coach_id: teamForm.coach_id || undefined,
        min_age: Number.isFinite(parsedMinAge) ? parsedMinAge : null,
      });
      setTeams(prev => [...prev, newTeam]);
      setTeamForm({ name: "", age_group_id: "", sport_id: "", coach_id: "", min_age: "" });
      toast.success(t("team.created", "Echipă creată cu succes"));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t("team.createError", "Nu s-a putut crea echipa"));
    }
    setCreating(false);
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
      <AdminPageHeader
        title={t("admin.teams", "Echipe")}
        subtitle={t("clubTeams.subtitle", "Creează și gestionează echipele clubului tău.")}
        icon={<ClubNavIcon id="teams" className="w-6 h-6" />}
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <SectionCard
            className="sticky top-6"
            title={t("admin.createTeam", "Creează echipă")}
            bodyPadded
          >
            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("common.name", "Nume")} *</label>
                <input
                  type="text"
                  required
                  value={teamForm.name}
                  onChange={e => setTeamForm({ ...teamForm, name: e.target.value })}
                  placeholder="ex. Junior A"
                  className="w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("team.ageGroup", "Grupă de vârstă")}</label>
                <select
                  value={teamForm.age_group_id}
                  onChange={e => setTeamForm({ ...teamForm, age_group_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">— {t("team.selectAgeGroup", "Selectează grupa de vârstă")} —</option>
                  {ageGroups.map(g => {
                    const bounds =
                      g.min_age == null && g.max_age != null ? ` (sub ${g.max_age + 1})` :
                      g.min_age != null && g.max_age == null ? ` (${g.min_age}+)` :
                      g.min_age != null && g.max_age != null ? ` (${g.min_age}–${g.max_age})` :
                      "";
                    return (
                      <option key={g.id} value={g.id}>{g.name}{bounds}</option>
                    );
                  })}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {t("team.ageGroupHint", "Grupele sunt definite global de platform admin. Vârsta maximă a grupei decide dacă datele părintelui sunt obligatorii.")}
                </p>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("team.minAge", "Vârsta minimă (opțional)")}
                </label>
                <input
                  type="number"
                  min={0}
                  max={99}
                  value={teamForm.min_age}
                  onChange={e => setTeamForm({ ...teamForm, min_age: e.target.value })}
                  placeholder="ex. 10"
                  className="w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {t("team.minAgeOverrideHint", "Lasă gol dacă folosești o grupă de vârstă. Setează aici doar pentru o limită numerică particulară.")}
                </p>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700" htmlFor="team-sport">
                  {t("team.sportType", "Sport")}
                </label>
                {/* Was a free-text box, so a team's sport never matched the ids
                    the club stores in its settings. Now the club's own list. */}
                <select
                  id="team-sport"
                  value={teamForm.sport_id}
                  onChange={e => setTeamForm({ ...teamForm, sport_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">— {t("team.noSport", "Fără sport")} —</option>
                  {sports.map(sp => (
                    <option key={sp.id} value={sp.id}>{sp.name}</option>
                  ))}
                </select>
                {sports.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">
                    {scope && scope.sport_ids !== null
                      ? t(
                          "team.noCoordinatorSports",
                          "Nu coordonezi niciun sport, deci nu poți crea echipe. Cere-i administratorului clubului să-ți aloce unul."
                        )
                      : t(
                          "team.noClubSports",
                          "Clubul nu are sporturi setate. Alege-le în Setări club ca să le poți folosi aici."
                        )}
                  </p>
                )}
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("team.coach", "Antrenor")} <span className="text-slate-400 font-normal">({t("common.optional", "opțional")})</span>
                </label>
                <select
                  value={teamForm.coach_id}
                  onChange={e => setTeamForm({ ...teamForm, coach_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">— {t("team.noCoachYet", "Fără antrenor (asignez mai târziu)")} —</option>
                  {coaches.map(c => (
                    <option key={c.id} value={c.user.id}>{c.user.full_name || c.user.email}</option>
                  ))}
                </select>
                {coaches.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {t("team.noCoachesHint", "Poți crea echipa acum și invita un antrenor mai târziu.")}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={creating || !teamForm.name}
                className="flex items-center justify-center w-full gap-2 px-6 py-3 font-medium text-white transition-colors bg-indigo-600 hover:bg-indigo-700 rounded-xl disabled:opacity-50"
              >
                {creating ? <div className="w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin"></div> : null}
                {t("common.create", "Creează")}
              </button>
            </form>
          </SectionCard>
        </div>
        <div className="lg:col-span-2">
          <SectionCard
            title={t("admin.teams", "Echipe")}
            actions={<span className="text-sm font-medium text-slate-500">{teams.length} {t("common.total", "total")}</span>}
            bodyPadded
          >
            {teams.length === 0 ? (
              <div className="py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-slate-700">{t("admin.noTeams", "Nicio echipă încă")}</h3>
                <p className="text-slate-500">{t("clubTeams.createFirst", "Creează prima echipă folosind formularul din stânga.")}</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {teams.map((tm) => (
                  <div key={tm.id} className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/admin-club/teams/${tm.id}`)}
                    className="p-4 text-left transition-all border bg-slate-50 rounded-t-xl border-slate-200 hover:border-indigo-300 hover:shadow-md group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 font-bold text-indigo-700 transition-colors bg-indigo-100 rounded-full group-hover:bg-indigo-200">
                        {tm.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold transition-colors truncate text-slate-800 group-hover:text-indigo-600">{tm.name}</h4>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {tm.age_group && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">{tm.age_group}</span>}
                          {tm.sport_type && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">{tm.sport_type}</span>}
                        </div>
                      </div>
                      <svg className="flex-shrink-0 w-5 h-5 transition-colors text-slate-300 group-hover:text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                  <div className="grid grid-cols-2 border border-t-0 bg-white border-slate-200 rounded-b-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setLinksFor(tm)}
                      className="px-3 py-2 text-xs font-semibold text-left text-indigo-600 hover:bg-indigo-50"
                    >
                      {t("team.shareLink", "Link de înscriere")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setProductsFor(tm)}
                      className="px-3 py-2 text-xs font-semibold text-left border-l text-slate-600 border-slate-200 hover:bg-slate-50"
                    >
                      {t("team.products", "Servicii / produse")}
                    </button>
                  </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
      </div>

      {productsFor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50">
          <div className="w-full max-w-2xl my-8 bg-white rounded-2xl">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">
                {t("team.products", "Servicii / produse")} — {productsFor.name}
              </h2>
              <button
                type="button"
                onClick={() => setProductsFor(null)}
                className="ml-auto text-2xl leading-none text-slate-400 hover:text-slate-600"
                aria-label={t("common.close", "Închide")}
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <TeamProducts teamId={productsFor.id} teamName={productsFor.name} />
            </div>
          </div>
        </div>
      )}

      {linksFor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50">
          <div className="w-full max-w-2xl my-8 bg-white rounded-2xl">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">
                {t("team.shareLinks", "Linkuri de înscriere")} — {linksFor.name}
              </h2>
              <button
                type="button"
                onClick={() => setLinksFor(null)}
                className="ml-auto text-2xl leading-none text-slate-400 hover:text-slate-600"
                aria-label={t("common.close", "Închide")}
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <TeamJoinLinks teamId={linksFor.id} teamName={linksFor.name} />
            </div>
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
